/**
 * Who is looking, for the quiet-path gate (03 §5.2): a cheap kinematic model of the garrison
 * built from the `MissionDef` alone. Sentries hold their posts and glance about; gunners watch
 * their arcs (a turret bunker sees all round); patrols walk their nodes at the sim's patrol
 * speed, pausing at each; searchlights sweep at night and at dusk. Every number that matters
 * is the sim's own (`ENEMY`, `SEARCHLIGHT`, `SPECIALS` in src/sim/tuning.ts).
 *
 * Time runs in steps of one metre of crawling (`stepSeconds`), so the search that uses these
 * looks can move the ranger one cell a step.
 */

import { atan2, PI } from '../../../sim/math';
import { ENEMY, RANGER, SEARCHLIGHT, SPECIALS } from '../../../sim/tuning';
import type { MissionDef, PropDef, SpawnDef, TimeOfDay } from '../../../sim/types';
import { QUIET } from '../../tuning';
import { handlerBeat, noseAt } from './nose';

const DEG = PI / 180;
const EIGHTH = PI / 4;

/** One metre of crawling. */
export const stepSeconds = 1 / RANGER.crawlSpeed;

/** One pair of eyes (or a lamp) at one moment. */
export interface Look {
  x: number;
  z: number;
  /** Centre of what he watches, radians (0 = south), and how far either side; PI = all round. */
  facing: number;
  half: number;
  range: number;
  /** Inside this he notices whichever way he faces (`ENEMY.senseRange`); 0 for gunners, machines and lamps. */
  sense: number;
  eye: number;
  /** His own emplacement or pole does not block his view. */
  ignoreProp: number;
  /** Standing in a depression himself he sees along it; nobody else sees a man crawling in one. */
  inTrench: boolean;
  /** ROUND8: a dog's nose (nose.ts) - it needs no line of sight. */
  smell?: boolean;
}

/** Someone whose look never changes. */
export interface Watcher extends Look {
  label: string;
}

/** Someone whose look repeats every `looks.length` steps. */
export interface Mover {
  label: string;
  looks: Look[];
}

export interface Garrison {
  watchers: Watcher[];
  movers: Mover[];
}

/** What the model needs of a map: little enough that the generator can ask about one it is still building. */
export interface GarrisonView {
  timeOfDay: TimeOfDay;
  props: readonly PropDef[];
  /** Is this point in a depression? (The gate answers from its raster of the sim's `inTrench`.) */
  inTrench(x: number, z: number): boolean;
}

const at = (x: number, z: number): string => `${Math.round(x)}, ${Math.round(z)}`;

/** A man who holds his post, as a watcher; null for what never sees a crawling man. Patrols are `garrisonOf`'s. */
export function watcherOf(def: GarrisonView, s: SpawnDef): Watcher | null {
  const range = ENEMY.range[def.timeOfDay];
  const base = { x: s.x, z: s.z, facing: s.facing, eye: QUIET.eye, inTrench: def.inTrench(s.x, s.z) };
  // A dog's nose walks with its handler: `garrisonOf` models it (nose.ts), and it holds no post of its own.
  if (s.kind === 'minitank' || s.kind === 'dog') return null;
  if (s.kind === 'miniBunker') {
    // A motion detector: it only notices what moves faster than a crawl (02 §9.2).
    if (RANGER.crawlSpeed <= SPECIALS.motionSpeed) return null;
    return { ...base, label: `the mini-bunker at ${at(s.x, s.z)}`, half: PI, range: SPECIALS.bunkerRadius, sense: 0,
      eye: 1, ignoreProp: -1, inTrench: true };
  }
  if (s.mans !== undefined) {
    const turret = def.props.find((p) => p.id === s.mans)?.kind === 'turretBunker';
    const half = turret ? PI : ENEMY.emplacementArcDeg * DEG;
    return { ...base, label: `the gunner at ${at(s.x, s.z)}`, half, range: range * 1.1, sense: 0, ignoreProp: s.mans };
  }
  const half = (ENEMY.fovDeg / 2) * DEG + QUIET.glance;
  return { ...base, label: `the ${s.kind} at ${at(s.x, s.z)}`, half, range, sense: ENEMY.senseRange, ignoreProp: -1 };
}

/** Grid steps between two nodes: the soldiers path 8-connected, so a leg is its octile length. */
function octile(a: readonly [number, number], b: readonly [number, number]): number {
  const dx = Math.abs(b[0] - a[0]);
  const dz = Math.abs(b[1] - a[1]);
  return Math.max(dx, dz) + (Math.SQRT2 - 1) * Math.min(dx, dz);
}

/**
 * What a man walking from a to b looks at. On the grid he alternates between the two compass
 * eighths either side of the leg, so unless the leg lies on one of them the cone is the union of both.
 */
function heading(a: readonly [number, number], b: readonly [number, number]): { facing: number; half: number } {
  const angle = atan2(b[0] - a[0], b[1] - a[1]);
  const half = (ENEMY.fovDeg / 2) * DEG + QUIET.headingSlack;
  const eighths = angle / EIGHTH;
  if (Math.abs(eighths - Math.round(eighths)) < 0.03) return { facing: angle, half };
  return { facing: (Math.floor(eighths) + 0.5) * EIGHTH, half: half + EIGHTH / 2 };
}

/** One loop of a patrol, a look per step: pause at a node, walk the leg to the next, and so on round. */
function patrolLooks(def: GarrisonView, nodes: ReadonlyArray<readonly [number, number]>, pad: number): Look[] {
  const range = ENEMY.range[def.timeOfDay] + pad;
  const count = nodes.length;
  // Timeline in seconds: [start of pause i, start of leg i, ...]; leg i runs from node i to node i + 1.
  const legStart: number[] = [];
  const legEnd: number[] = [];
  let clock = 0;
  for (let i = 0; i < count; i++) {
    const a = nodes[i] ?? [0, 0];
    const b = nodes[(i + 1) % count] ?? a;
    clock += QUIET.dwellSeconds;
    legStart.push(clock);
    clock += octile(a, b) / ENEMY.walkSpeed;
    legEnd.push(clock);
  }
  const steps = Math.max(1, Math.ceil(clock / stepSeconds));
  const looks: Look[] = [];
  let leg = 0;
  for (let k = 0; k < steps; k++) {
    const t = k * stepSeconds;
    while (leg < count - 1 && t >= (legEnd[leg] ?? Infinity)) leg++;
    const a = nodes[leg] ?? [0, 0];
    const b = nodes[(leg + 1) % count] ?? a;
    const start = legStart[leg] ?? 0;
    const end = legEnd[leg] ?? start;
    // Pausing at node `leg` he still faces the way he arrived; past the last leg he is back at node 0.
    const pausing = t < start;
    const home = t >= end;
    const from = pausing ? nodes[(leg + count - 1) % count] ?? a : a;
    const to = pausing ? a : b;
    const f = pausing || end <= start ? 0 : Math.min(1, (t - start) / (end - start));
    const x = home ? b[0] : pausing ? a[0] : a[0] + (b[0] - a[0]) * f;
    const z = home ? b[1] : pausing ? a[1] : a[1] + (b[1] - a[1]) * f;
    looks.push({
      x, z, ...heading(from, to), range, sense: ENEMY.senseRange + pad, eye: QUIET.eye, ignoreProp: -1,
      inTrench: def.inTrench(x, z),
    });
  }
  return looks;
}

/** Everything a lamp's beam ever touches, as one look: for siting lamps. The gate itself follows the sweep. */
export function lampSweep(prop: { id: number; x: number; z: number; yaw: number }): Look {
  return {
    x: prop.x, z: prop.z, facing: prop.yaw, half: (SEARCHLIGHT.arcDeg / 2 + SEARCHLIGHT.halfAngleDeg) * DEG,
    range: SEARCHLIGHT.range, sense: 0, eye: SEARCHLIGHT.lampHeight, ignoreProp: prop.id, inTrench: false,
  };
}

/** One there-and-back sweep of a lamp (hazards/searchlights.ts), a look per step, starting where the sim starts it. */
function lampLooks(prop: { id: number; x: number; z: number; yaw: number }): Look[] {
  const arc = SEARCHLIGHT.arcDeg * DEG;
  const rate = SEARCHLIGHT.sweepDegPerSecond * DEG;
  const steps = Math.max(1, Math.round((2 * arc) / rate / stepSeconds));
  // Position along the there-and-back sweep, 0..2 arc; the beam's bearing folds it back at the far end.
  const phase = ((prop.id * 37) % 100) / 100;
  const origin = prop.id % 2 === 0 ? phase * arc : 2 * arc - phase * arc;
  const bearing = (s: number): number => {
    const u = ((s % (2 * arc)) + 2 * arc) % (2 * arc);
    return prop.yaw - arc / 2 + (u <= arc ? u : 2 * arc - u);
  };
  const looks: Look[] = [];
  for (let k = 0; k < steps; k++) {
    // Everything the beam touches during the step: sample it and take the spread.
    let lo = Infinity;
    let hi = -Infinity;
    for (let sub = 0; sub <= 4; sub++) {
      const b = bearing(origin + (k + sub / 4) * stepSeconds * rate);
      lo = Math.min(lo, b);
      hi = Math.max(hi, b);
    }
    looks.push({
      x: prop.x, z: prop.z, facing: (lo + hi) / 2, half: (hi - lo) / 2 + SEARCHLIGHT.halfAngleDeg * DEG,
      range: SEARCHLIGHT.range, sense: 0, eye: SEARCHLIGHT.lampHeight, ignoreProp: prop.id, inTrench: false,
    });
  }
  return looks;
}

/** Do two routes run side by side, node for node? Then they are one patrol walking in file. */
function sameFile(a: SpawnDef, b: SpawnDef): number {
  const pa = a.patrol ?? [];
  const pb = b.patrol ?? [];
  if (pa.length !== pb.length) return -1;
  let worst = 0;
  for (let i = 0; i < pa.length; i++) {
    const p = pa[i] ?? [0, 0];
    const q = pb[i] ?? [0, 0];
    worst = Math.max(worst, Math.sqrt((p[0] - q[0]) * (p[0] - q[0]) + (p[1] - q[1]) * (p[1] - q[1])));
  }
  return worst <= QUIET.fileReach ? worst : -1;
}

/** Everyone on the map at the start who could see a crawling man, and every lamp that could light him. */
export function garrisonOf(def: MissionDef, inTrench: GarrisonView['inTrench']): Garrison {
  const view: GarrisonView = { timeOfDay: def.timeOfDay, props: def.props, inTrench };
  const watchers: Watcher[] = [];
  const movers: Mover[] = [];
  let lead: SpawnDef | null = null;
  let file: { lead: SpawnDef; pad: number } | null = null;
  const files: Array<{ lead: SpawnDef; pad: number }> = [];
  for (const s of def.spawns) {
    if (s.dormantUntil !== undefined) continue;
    if (s.mans === undefined && s.patrol !== undefined && s.patrol.length > 1 && s.kind !== 'miniBunker') {
      // A patrol in file is watched for from its lead man, his range padded by how far the others walk from him.
      const gap = lead && file ? sameFile(lead, s) : -1;
      if (file && gap >= 0) file.pad = Math.max(file.pad, gap);
      else {
        lead = s;
        file = { lead: s, pad: 0 };
        files.push(file);
      }
      continue;
    }
    lead = null;
    file = null;
    const w = watcherOf(view, s);
    if (w) watchers.push(w);
  }
  for (const f of files) {
    const looks = patrolLooks(view, f.lead.patrol ?? [], f.pad);
    movers.push({ label: `the patrol from ${at(f.lead.x, f.lead.z)}`, looks });
  }
  // ROUND8: every dog, as a nose that walks its handler's beat - or stands still where there is no beat.
  for (const s of def.spawns) {
    if (s.kind !== 'dog' || s.dormantUntil !== undefined) continue;
    const beat = handlerBeat(def, s);
    const label = beat ? `the dog team from ${at(s.x, s.z)}` : `the dog at ${at(s.x, s.z)}`;
    if (beat) movers.push({ label, looks: patrolLooks(view, beat, 0).map(noseAt) });
    else watchers.push({ ...noseAt(s), label });
  }
  if (def.timeOfDay === 'night' || def.timeOfDay === 'dusk') {
    for (const p of def.props) {
      if (p.kind === 'searchlight') movers.push({ label: `the searchlight at ${at(p.x, p.z)}`, looks: lampLooks(p) });
    }
  }
  return { watchers, movers };
}
